"use client"

import { useState, useEffect, useRef } from "react"
import "./QAProcess.css"

const NearProcess = () => {
  const [activeTab, setActiveTab] = useState("profiling")
  const headerRef = useRef(null)
  const textRef = useRef(null)
  const rightTextRef = useRef(null)
  const imageRef = useRef(null)
  const tabsRef = useRef(null)

  const tabContent = {
    profiling: {
      title: "ASSESMENT      ",
      subtitle1: "1. QA Readiness Assessment",
      subtitle2: "2. Test Plan",
      
      description: [
        "As the first step in our Quality Assurance (QA) process, we conduct a thorough review of the existing QA setup. We aim to gain a comprehensive understanding of the project's quality requirements. This involves scrutinizing the various facets of the project to ensure that our QA process encompasses all critical aspects.Following the QA readiness assessment, we create a detailed test plan. This document serves as a roadmap for the entire testing phase. It outlines the objectives and scope of testing, procedures for test scenario development, requirements for the test environment, and the resources needed. This comprehensive test plan serves as our guide throughout the QA process.",
      ],
    },
    enable: {
      title: "BOOTSTRAP",
      subtitle1:"1. Test Environment Setup",
      subtitle2: "2. Automation Testing Setup",
      items: [
        "In this step, we focus on setting up or updating a test environment that closely mirrors the production environment. This includes configuring hardware, software, and various settings. We also enable code for mocking and dependency injection to ensure that our testing environment is as realistic as possible.Leveraging advanced automation testing tools, we establish a framework to enhance efficiency and reduce the potential for human error. This involves carefully selecting appropriate tools, configuring automation settings, and ensuring seamless integration into our testing environment."
      ],
    },
    development: {
      title: "BACKLOG",
      subtitle1:"1. Product Backlog Update",
      subtitle2:"2. Acceptance Sessions",
      
      items: [
        "As new items are added to the product backlog, we update it with relevant QA-related information. This includes reviewing and updating test cases to accommodate any changes or additions to the backlog. This step ensures that our testing efforts remain aligned with the evolving project requirements.Once the QA team is confident that all test cases have been executed successfully, we involve stakeholders or end-users in acceptance sessions. These sessions aim to validate the project against the original requirements, ensuring that it meets the expectations and standards set by the project stakeholders.",
      ],
    },
    phase: {
      title: "QA Sprint 1..n",
      subtitle1:"1. Test Cases Design",
      subtitle2:"2. Test Cases Execution (Verification & Validation)",
      subtitle3:"3. Improvements Identification",
      items: [
        "Based on the comprehensive test plan, we meticulously design test cases that cover both functional and non-functional requirements. These test cases are formulated to be clear, concise, and to encompass all possible scenarios, ensuring a thorough examination of the project's functionality.This stage involves the systematic execution of the designed test cases. We verify the functionality of the project, capturing detailed test results, including any issues or defects encountered during testing. Each defect is carefully assigned severity and priority levels, and we track their resolution progress. The validation process ensures that the project meets the specified requirements.Throughout the QA process, we conduct regular reviews and non-planned testing to identify and address potential bottlenecks and inefficiencies. This ongoing assessment helps us refine our QA testing process and ensures continuous improvement.",
      ],
    },
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible")
          }
        })
      },
      { threshold: 0.1 },
    )

    const elements = [headerRef.current, textRef.current, rightTextRef.current, imageRef.current, tabsRef.current]
    elements.forEach((el) => {
      if (el) observer.observe(el)
    })

    return () => {
      elements.forEach((el) => {
        if (el) observer.unobserve(el)
      })
    }
  }, [])

  return (
    <div className="scoping-container" id="process">
      <div className="container">
      <div className="scoping-wrapper">
        {/* Header */}

        {/* Main Content */}
        {/* Main Content - 2x2 Grid */}
        <div className="main-content-grid">
          {/* Top Row */}
          <div className="header-title-section" ref={headerRef}>
            <h2 className="header-title6144">
              OUR
              <br />
              <span className="highlight">QUALITY ASSURANCE</span>
              <br />
              <span className="highlight">PROCESS</span>
            </h2>
          </div>
          <div className="top-right-text" ref={rightTextRef}>
            <p className="right-side-text">
              Our Quality Assurance focuses on prevention rather than just detection. By adhering to rigorous testing and quality standards, we guarantee error-free software development. This holistic approach considers not only the end user's experience but also aligns with business requirements, fostering customer satisfaction and a sterling reputation.
            </p>
          </div>

          {/* Bottom Row */}
          <div className="text-section" ref={textRef}>
            <p className="main-description">
              By implementing the QA, we minimize the risk of usability issues or any unforeseen problems during the customer adoption or rollout phases.


            </p>
          </div>
          <div className="image-section" ref={imageRef}>
            <img
              src="/process-scoping.webp?height=300&width=400"
              alt="Nearshore Process Illustration"
              className="process-image"
            />
          </div>
        </div>

        {/* Tabs Section */}
        <div className="tabs-container" ref={tabsRef}>
          <div className="tabs-header">
            <button
              className={`tab-button ${activeTab === "profiling" ? "active" : ""}`}
              onClick={() => setActiveTab("profiling")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              ASSESEMENT
            </button>
            <button
              className={`tab-button ${activeTab === "enable" ? "active" : ""}`}
              onClick={() => setActiveTab("enable")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              BOOTSTRAP
            </button>
            <button
              className={`tab-button ${activeTab === "development" ? "active" : ""}`}
              onClick={() => setActiveTab("development")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </div>
              BACKLOG
            </button>
            <button
              className={`tab-button ${activeTab === "phase" ? "active" : ""}`}
              onClick={() => setActiveTab("phase")}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <rect x="7" y="7" width="3" height="9" />
                  <rect x="14" y="7" width="3" height="5" />
                </svg>
              </div>
              QA Sprint 1..n
            </button>
          </div>

          <div className="tab-content">
            <div className={`tab-panel ${activeTab === "profiling" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.profiling.title}</h3>
              <h4 className="tab-subtitle874">{tabContent.profiling.subtitle1}</h4>
               <h4 className="tab-subtitle874">{tabContent.profiling.subtitle2}</h4>
              
              <p className="tab-description">{tabContent.profiling.description[0]}</p>
              <ul className="tab-list">
                {tabContent.profiling.description.slice(1).map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "enable" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.enable.title}</h3>
             <h4 className="tab-subtitle874">{tabContent.enable.subtitle1}</h4>
             <h4 className="tab-subtitle874">{tabContent.enable.subtitle2}</h4>

              <ul className="tab-list">
                {tabContent.enable.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "development" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.development.title}</h3>
              <h4 className="tab-subtitle874">{tabContent.development.subtitle1}</h4>
              <h4 className="tab-subtitle874">{tabContent.development.subtitle2}</h4>
             
              <ul className="tab-list">
                {tabContent.development.items.map((item, index) => (
                  <li key={index} className="tab-item">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`tab-panel ${activeTab === "phase" ? "active" : ""}`}>
              <h3 className="tab-title">{tabContent.phase.title}</h3>
              <h4 className="tab-subtitle874">{tabContent.phase.subtitle1}</h4>
              <h4 className="tab-subtitle874">{tabContent.phase.subtitle2}</h4>
              <ul className="tab-list">
                {tabContent.phase.items.map((item, index) => (
                  <li key={index} className="tab-item845">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section">
          <h3 className="cta-title">NEED A QUOTE FOR YOUR PROJECT?</h3>
          <p className="cta-description">
            Our business developers, project managers and software engineers are ready to clarify any questions you have
            related. Feel free to chat with us anytime.
          </p>
          <button className="quote-button">GET A QUOTE</button>
        </div>
      </div>
    </div>
    </div>
  )
}

export default NearProcess
